import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { hasUnsavedWork } from '@/lib/unsavedWork';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    const msg = error.message || '';
    const isStaleChunk =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Loading chunk') ||
      msg.includes('is not a valid JavaScript MIME type') ||
      (error.name === 'TypeError' && msg.includes('Failed to fetch'));
    // Stale-chunk errors are self-healing (we reload below) — don't report them;
    // capturing just re-opens a "regressed" Sentry issue on every deploy
    // (CPMS-WEB-6 / CPMS-WEB-4). Report everything else.
    if (!isStaleChunk) {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: info.componentStack ?? '' } },
      });
    }
    // Auto-reload on stale chunk ONLY when there is no unsaved work; otherwise
    // a reload would silently discard a half-filled form / staged photos. When
    // dirty we fall through to the manual "Refresh" fallback (rendered below).
    if (isStaleChunk && !hasUnsavedWork()) {
      const key = 'eb_reload_ts';
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Ralat / Error</h1>
            <p className="text-gray-600 mb-6">
              Sesuatu telah berlaku. Sila muat semula halaman.
              <br />
              Something went wrong. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Muat Semula / Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
